import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { GraphicEq, Stop, FiberManualRecord, Sync, Pause, PlayArrow } from '@mui/icons-material';
import { consultationService } from '../../services/consultationService';

const CHUNK_DURATION_MS = 60_000;

interface AmbientRecorderProps {
  consultationId: string | undefined;
  existingReport: string;
  language?: string;
  onReportUpdate: (report: string, extractedInfo: any) => void;
  onSessionComplete?: (finalReport: string) => void;
  disabled?: boolean;
  resetTrigger?: number;
}

type AmbientStatus = 'idle' | 'recording' | 'paused' | 'processing';

export const AmbientRecorder = ({
  consultationId,
  existingReport,
  language = 'fr',
  onReportUpdate,
  onSessionComplete,
  disabled = false,
  resetTrigger,
}: AmbientRecorderProps) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AmbientStatus>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [chunkCount, setChunkCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const isActiveRef = useRef(false);
  const chunkTimerRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track recorded ms in current chunk for accurate pause/resume of the chunk timer
  const chunkStartTimeRef = useRef(0);
  const chunkRecordedMsRef = useRef(0);
  // Tail of the previous chunk's raw transcription for cross-boundary context (~last 300 chars)
  const previousTailRef = useRef('');
  // Full accumulated raw transcription across all chunks (for final consolidation pass)
  const allTranscriptionsRef = useRef('');

  const prevResetTriggerRef = useRef(resetTrigger);

  const latestReportRef = useRef(existingReport);
  useEffect(() => {
    latestReportRef.current = existingReport;
  }, [existingReport]);

  const hardReset = useCallback(() => {
    isActiveRef.current = false;
    if (chunkTimerRef.current) { clearTimeout(chunkTimerRef.current); chunkTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    allTranscriptionsRef.current = '';
    previousTailRef.current = '';
    setStatus('idle');
    setElapsedSeconds(0);
    setChunkCount(0);
    setError(null);
    setIsFinishing(false);
  }, []);

  // Reset when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger !== prevResetTriggerRef.current) {
      prevResetTriggerRef.current = resetTrigger;
      hardReset();
    }
  }, [resetTrigger, hardReset]);

  useEffect(() => { return () => { hardReset(); }; }, [hardReset]);

  const sendChunk = useCallback(async (blob: Blob, chunkIndex: number, isLastChunk: boolean) => {
    if (!consultationId || blob.size === 0) {
      if (isLastChunk) { setIsFinishing(false); setStatus('idle'); onSessionComplete?.(latestReportRef.current); }
      return;
    }

    setStatus('processing');
    try {
      const result = await consultationService.transcribeAmbientChunk(
        consultationId,
        blob,
        latestReportRef.current,
        language,
        previousTailRef.current,
      );

      // Accumulate raw transcription for the final consolidation pass
      if (result.chunk_transcription) {
        previousTailRef.current = result.chunk_transcription.slice(-300);
        allTranscriptionsRef.current = (allTranscriptionsRef.current + ' ' + result.chunk_transcription).trim();
      }

      onReportUpdate(result.updated_report, {
        name: result.patient_name,
        species: result.patient_species,
        breed: result.patient_breed,
        age: result.patient_age,
        sex: result.patient_gender,
        sterilization_status: result.sterilization_status,
        weight: result.patient_weight,
        drugs: result.extracted_drugs,
      });

      if (isLastChunk) {
        // Final consolidation pass: fix contradictions + consolidate all bullets
        try {
          const finalResult = await consultationService.finalizeAmbientSession(
            consultationId,
            allTranscriptionsRef.current,
            result.updated_report,
            language,
          );
          onReportUpdate(finalResult.final_report, {});
          onSessionComplete?.(finalResult.final_report);
        } catch (finalErr) {
          console.error('Ambient finalization error (non-fatal):', finalErr);
          onSessionComplete?.(result.updated_report);
        }
      }
    } catch (err: any) {
      console.error('Ambient chunk error:', err);
      setError(err.response?.data?.detail || t('consultation.ambient.chunkError'));
      if (isLastChunk) onSessionComplete?.(latestReportRef.current);
    } finally {
      if (isLastChunk) {
        setIsFinishing(false);
        setStatus('idle');
      } else if (isActiveRef.current) {
        setStatus('recording');
      }
    }
  }, [consultationId, language, onReportUpdate, onSessionComplete, t]);

  const startChunkRecording = useCallback(() => {
    if (!isActiveRef.current || !streamRef.current) return;

    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 32000,
    });
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];
    chunkRecordedMsRef.current = 0;
    chunkStartTimeRef.current = Date.now();

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      chunksRef.current = [];
      const isLastChunk = !isActiveRef.current;

      setChunkCount((prev) => {
        const newCount = prev + 1;
        sendChunk(blob, newCount, isLastChunk);
        return newCount;
      });

      if (!isLastChunk) {
        startChunkRecording();
      }
    };

    recorder.start();

    chunkTimerRef.current = setTimeout(() => {
      if (recorder.state === 'recording') recorder.stop();
    }, CHUNK_DURATION_MS);
  }, [sendChunk]);

  const startSession = async () => {
    if (!consultationId) { setError(t('consultation.notStarted')); return; }
    setError(null);
    setElapsedSeconds(0);
    setChunkCount(0);
    setIsFinishing(false);
    previousTailRef.current = '';
    allTranscriptionsRef.current = '';

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      isActiveRef.current = true;
      setStatus('recording');

      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);

      startChunkRecording();
    } catch {
      setError(t('errors.errorMicrophoneAccess'));
    }
  };

  const pauseSession = () => {
    if (mediaRecorderRef.current?.state !== 'recording') return;

    // Snapshot how much of the 60s window has been used
    chunkRecordedMsRef.current += Date.now() - chunkStartTimeRef.current;

    mediaRecorderRef.current.pause();
    setStatus('paused');

    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (chunkTimerRef.current) { clearTimeout(chunkTimerRef.current); chunkTimerRef.current = null; }
  };

  const resumeSession = () => {
    if (mediaRecorderRef.current?.state !== 'paused') return;

    chunkStartTimeRef.current = Date.now();
    const remaining = Math.max(CHUNK_DURATION_MS - chunkRecordedMsRef.current, 2000);

    const recorder = mediaRecorderRef.current;
    recorder.resume();
    setStatus('recording');

    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);

    chunkTimerRef.current = setTimeout(() => {
      if (recorder.state === 'recording') recorder.stop();
    }, remaining);
  };

  const stopSession = () => {
    isActiveRef.current = false;
    setIsFinishing(true);

    if (chunkTimerRef.current) { clearTimeout(chunkTimerRef.current); chunkTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop(); // triggers onstop → sends last chunk → finally sets idle
    } else {
      // No recorder active (edge case), go straight to idle
      setIsFinishing(false);
      setStatus('idle');
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const isActive = status !== 'idle';
  const isPaused = status === 'paused';
  const isProcessing = status === 'processing';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: isActive ? 'success.main' : 'divider',
        borderRadius: 1,
        transition: 'border-color 0.3s',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isActive ? (
        <Tooltip title={t('consultation.ambient.tooltip')} placement="top">
          <span style={{ display: 'block' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<GraphicEq />}
              onClick={startSession}
              disabled={disabled || !consultationId}
              size="medium"
              fullWidth
            >
              {t('consultation.ambient.startButton')}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Stop */}
          <IconButton
            color="error"
            onClick={stopSession}
            disabled={isFinishing}
            size="medium"
            sx={{ border: 2, borderColor: isFinishing ? 'action.disabled' : 'error.main', flexShrink: 0 }}
          >
            <Stop />
          </IconButton>

          {/* Pause / Resume */}
          {!isFinishing && (
            <IconButton
              color="primary"
              onClick={isPaused ? resumeSession : pauseSession}
              disabled={isProcessing}
              size="medium"
              sx={{ flexShrink: 0 }}
            >
              {isPaused ? <PlayArrow /> : <Pause />}
            </IconButton>
          )}

          {/* Status indicators + progress */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {isProcessing && (
              <Sync sx={{ color: 'warning.main', fontSize: 14, animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }, flexShrink: 0 }} />
            )}
            {status === 'recording' && (
              <FiberManualRecord sx={{ color: 'success.main', fontSize: 14, animation: 'pulse 1.2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }, flexShrink: 0 }} />
            )}
            {isPaused && (
              <Pause sx={{ color: 'text.secondary', fontSize: 14, flexShrink: 0 }} />
            )}

            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flexShrink: 0 }}
              color={isProcessing ? 'warning.main' : isPaused ? 'text.secondary' : 'success.main'}
            >
              {formatTime(elapsedSeconds)}
            </Typography>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LinearProgress
                variant={isProcessing ? 'indeterminate' : isPaused ? 'determinate' : 'determinate'}
                value={isProcessing || isPaused ? undefined : ((elapsedSeconds % 60) / 60) * 100}
                color={isProcessing ? 'warning' : 'success'}
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Box>

            {chunkCount > 0 && (
              <Chip
                label={t('consultation.ambient.segmentCount', { count: chunkCount })}
                size="small"
                color={isProcessing ? 'warning' : 'success'}
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            )}
          </Box>
        </Box>
      )}

      {isProcessing && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          {t('consultation.ambient.updating')}
        </Typography>
      )}
    </Paper>
  );
};
